import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'kn' | 'hi' | 'ta' | 'te' | 'mr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const languages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
];

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>('en');

  useEffect(() => {
    const savedLang = localStorage.getItem('kalpakrushi-language') as Language;
    if (savedLang && languages.some(l => l.code === savedLang)) {
      setLanguageState(savedLang);
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('kalpakrushi-language', lang);
  };

  const t = (key: string): string => {
    // support keys like 'a.b.c' and 'a.list[0].name'
    const pattern = /[^.[\]]+|\[(\d+)\]/g;
    const parts: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(key)) !== null) {
      if (match[0].startsWith('[')) {
        parts.push(match[1]);
      } else {
        parts.push(match[0]);
      }
    }

    let value: any = translations[language];
    for (const k of parts) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key;
      }
    }

    return typeof value === 'string' ? value : key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}

// Comprehensive translations
const translations: Record<Language, any> = {
  en: {
    nav: {
      home: 'Home',
      services: 'Services',
      about: 'About',
      projects: 'Projects',
      contact: 'Contact',
    },
    hero: {
      title: 'Transform Your Farm with',
      titleHighlight: 'Smart Agriculture',
      success: 'ಧನ್ಯವಾದಗಳು! ನಿಮ್ಮ ಸಂದೇಶವನ್ನು ಯಶಸ್ವಿಯಾಗಿ ಕಳುಹಿಸಲಾಗಿದೆ.',
      error: 'ಕ್ಷಮಿಸಿ, ನಿಮ್ಮ ಸಂದೇಶ ಕಳುಹಿಸುವಲ್ಲಿ ದೋಷವಾಯಿತು. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
      callUs: 'ನಮಗೆ ಕರೆ ಮಾಡಿ',
      emailUs: 'ಇಮೇಲ್ ಮಾಡಿ',
      visitUs: 'ನಮ್ಮನ್ನು ಭೇಟಿ ಮಾಡಿ',
      businessHours: 'ವ್ಯವಹಾರ ಸಮಯ',
      subtitle: 'Revolutionizing Indian farming with cutting-edge technology, sustainable practices, and expert guidance for maximum yields',
      cta: 'Get Started',
      ctaSecondary: 'Learn More',
    },
    services: {
      title: 'Our Premium Services',
      subtitle: 'Comprehensive agricultural solutions tailored for modern Indian farming',
      farmDevelopment: {
        title: 'Complete Farm Development',
        description: 'Transform your land into a high-productivity agricultural asset',
      },
      soilTesting: {
        title: 'Soil Testing & Crop Advisory',
        description: 'Scientific soil analysis and customized crop recommendations',
      },
      precisionAgriculture: {
        title: 'Precision Agriculture',
        description: 'Advanced technology for optimized farming operations',
      },
      digitalFarming: {
        title: 'Digital Farming Solutions',
        description: 'Smart apps and IoT devices for modern farm management',
      },
      nursery: {
        title: 'Nursery & Plantation',
        description: 'Premium quality saplings and plantation expertise',
      },
      irrigation: {
        title: 'Advanced Irrigation',
        description: 'Water-efficient irrigation systems and management',
      },
      // Detailed irrigation cards for the irrigation solutions component
      irrigationCards: [
        {
          title: 'Drip Irrigation Design',
          description: 'Precision water delivery system with 90% water efficiency. Custom design for every crop type and farm size.',
          features: ['Water Savings', 'Uniform Distribution', 'Fertilizer Integration', 'Automated Control']
        },
        {
          title: 'Sprinkler Systems',
          description: 'Uniform water distribution for large areas. Ideal for field crops, vegetables, and orchards.',
          features: ['Large Coverage', 'Adjustable Pressure', 'Energy Efficient', 'Easy Maintenance']
        },
        {
          title: 'Borewell Analysis',
          description: 'Scientific water source evaluation, quality testing, and yield optimization for sustainable water usage.',
          features: ['Water Quality Test', 'Yield Analysis', 'Depth Recommendation', 'Sustainability Check']
        },
        {
          title: 'Rainwater Harvesting',
          description: 'Capture and store rainwater for irrigation. Reduce dependency on groundwater and ensure year-round availability.',
          features: ['Storage Solutions', 'Recharge Systems', 'Filter Design', 'Cost Effective']
        }
      ],
      machinery: {
        title: 'Farm Machinery & Equipment',
        description: 'Modern agricultural equipment and tools supply',
      },
      marketing: {
        title: 'Marketing & Post-Harvest',
        description: 'Market linkage and value addition services',
      },
      learnMore: 'Learn More',
    },
    whyChoose: {
      title: 'Why Choose Kalpakrushi',
      subtitle: 'Leading AgriTech innovation in India',
      expert: {
        title: 'Expert Team',
        description: '15+ years of agricultural expertise',
      },
      technology: {
        title: 'Advanced Technology',
        description: 'Latest IoT and AI solutions',
      },
      support: {
        title: '24/7 Support',
        description: 'Round-the-clock farmer assistance',
      },
      proven: {
        title: 'Proven Results',
        description: '500+ successful projects',
      },
    },
    projects: {
      title: 'Our Success Stories',
      subtitle: 'Real transformations across India',
      viewProject: 'View Details',
      list: [
        {
          title: 'Mixed Farming Layout - 50 Acres',
          location: 'Karnataka',
          type: 'Farm Development'
        },
        {
          title: 'Drip Irrigation System Design',
          location: 'Tiptur Region',
          type: 'Irrigation'
        },
        {
          title: 'Commercial Polyhouse Setup',
          location: 'Hassan District',
          type: 'Infrastructure'
        },
        {
          title: 'Precision Agriculture - Drone Services',
          location: 'Multiple Locations',
          type: 'Technology'
        },
        {
          title: 'Soil Testing & Advisory Program',
          location: 'Tumkur Region',
          type: 'Advisory'
        },
        {
          title: 'Modern Equipment Implementation',
          location: 'Sharada Nagar, Tiptur',
          type: 'Equipment'
        }
      ]
    },
    about: {
      title: 'About Kalpakrushi',
      subtitle: 'Pioneering sustainable agriculture',
      description: 'Kalpakrushi is revolutionizing Indian agriculture through innovative technology and sustainable practices. We empower farmers with cutting-edge solutions for enhanced productivity and profitability.',
      mission: {
        title: 'Our Mission',
        text: 'To create a technology-enabled agricultural ecosystem where every farmer can make informed decisions, achieve sustainable growth, and secure a better future.'
      },
      vision: {
        title: 'Our Vision',
        text: "To create a technology-enabled agricultural ecosystem where every farmer can make informed decisions, achieve sustainable growth, and secure a better future."
      },
      milestones: [
        { year: '2024', title: 'Company Founded', desc: 'Started with a vision to modernize agriculture' },
        { year: '2024', title: '50+ Farms', desc: 'Successfully developed and transformed farms' },
        { year: '2024', title: '150+ Acres', desc: 'Acres under professional management' },
        { year: '2025', title: '50+ Farms', desc: 'Milestone achievement in farm development' }
      ],
      achievements: [
        { title: '150+ Acres', desc: 'Under professional management' },
        { title: '50+ Farms', desc: 'Successfully developed and transformed' },
        { title: '2024-2025', desc: 'Rapid growth and expansion' }
      ],
      stats: {
        projects: 'Projects Completed',
        farmers: 'Farmers Helped',
        hectares: 'Hectares Transformed',
        states: 'States Covered',
      },
    },
    irrigation: {
      title: 'Advanced Irrigation Solutions',
      subtitle: 'Water-efficient farming systems',
      drip: 'Drip Irrigation',
      sprinkler: 'Sprinkler Systems',
      automation: 'Smart Automation',
      getQuote: 'Get Quote',
    },
    digitalApp: {
      title: 'Kalpakrushi Digital App',
      subtitle: 'Farm management at your fingertips',
      monitoring: 'Real-time Monitoring',
      weather: 'Weather Forecasts',
      advisory: 'Expert Advisory',
      marketplace: 'Marketplace',
      download: 'Download Now',
    },
    globalServices: {
      title: 'Global Service, Happy to Serve',
      subtitle: "We're committed to delivering world-class agricultural solutions",
      list: [
        { title: 'Global Service, Happy to Serve', description: 'We provide professional agricultural services across multiple regions, bringing excellence wherever we go.' },
        { title: 'Award-Winning Excellence', description: 'Recognized leaders with awards for innovative and sustainable practices.' },
        { title: 'Customer-Centric Approach', description: 'Your satisfaction is our priority; we work closely to bring your vision to life.' }
      ]
    },
    testimonials: {
      title: 'What Farmers Say',
      subtitle: 'Trusted by thousands across India',
      list: [
        {
          name: 'Rajesh Kumar',
          role: 'Farmer - 30 Acres',
          location: 'Tiptur, Karnataka',
          text: 'Kalpakrushi completely transformed my farm. The drip irrigation saved water and increased yields; their app keeps me informed in real-time.'
        },
        {
          name: 'Manjunath Gowda',
          role: 'Commercial Farmer',
          location: 'Hassan District',
          text: 'Precision agriculture and drone services were a game-changer. Soil testing and advisory helped us make data-driven decisions.'
        },
        {
          name: 'Lakshmi Devi',
          role: 'Organic Farm Owner',
          location: 'Tumkur Region',
          text: 'From planning to execution, Kalpakrushi delivered sustainable solutions that matched our organic farming needs.'
        },
        {
          name: 'Suresh Reddy',
          role: 'Multi-Crop Farmer',
          location: 'Sharada Nagar',
          text: "Their subsidy guidance and polyhouse setup increased my income significantly. Highly recommended."
        }
      ]
    },
    contact: {
      title: 'Get In Touch',
      subtitle: 'Ready to transform your farm? Contact us today for a free consultation and custom farming solutions',
      form: {
        name: 'Your Name',
        email: 'Email Address',
        phone: 'Phone Number',
        location: 'farm location',
        message: 'Your Message',
        submit: 'Send Message',
      },
      success: 'Thank you! Your message has been sent successfully.',
      error: 'Sorry, there was an error sending your message. Please try again.',
      callUs: 'Call Us',
      emailUs: 'Email Us',
      visitUs: 'Visit Us',
      businessHours: 'Business Hours',
      address: 'Sharada Nagar, Tiptur, Karnataka',
    },
    footer: {
      description: 'Transforming Indian agriculture with smart technology and sustainable farming solutions.',
      quickLinks: 'Quick Links',
      services: 'Our Services',
      contactInfo: 'Contact Info',
      copyright: '© 2025 Kalpakrushi AgriTech. All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      sitemap: 'Sitemap',
    },
    common: {
      readMore: 'Read More',
      getStarted: 'Get Started',
      contactUs: 'Contact Us',
      viewAll: 'View All',
    },
  },
  kn: {
    nav: {
      home: 'ಮುಖಪುಟ',
      services: 'ಸೇವೆಗಳು',
      about: 'ನಮ್ಮ ಬಗ್ಗೆ',
      projects: 'ಯೋಜನೆಗಳು',
      contact: 'ಸಂಪರ್ಕಿಸಿ',
    },
    hero: {
      title: 'ನಿಮ್ಮ ಜಮೀನನ್ನು ಪರಿವರ್ತಿಸಿ',
      titleHighlight: 'ಸ್ಮಾರ್ಟ್ ಕೃಷಿಯೊಂದಿಗೆ',
      subtitle: 'ಅತ್ಯಾಧುನಿಕ ತಂತ್ರಜ್ಞಾನ, ಸುಸ್ಥಿರ ಅಭ್ಯಾಸಗಳು ಮತ್ತು ಗರಿಷ್ಠ ಇಳುವರಿಗಾಗಿ ತಜ್ಞರ ಮಾರ್ಗದರ್ಶನದೊಂದಿಗೆ ಭಾರತೀಯ ಕೃಷಿಯನ್ನು ಕ್ರಾಂತಿಗೊಳಿಸುವುದು',
      cta: 'ಪ್ರಾರಂಭಿಸಿ',
      ctaSecondary: 'ಇನ್ನಷ್ಟು ತಿಳಿಯಿರಿ',
    },
    services: {
      title: 'ನಮ್ಮ ಪ್ರೀಮಿಯಂ ಸೇವೆಗಳು',
      subtitle: 'ಆಧುನಿಕ ಭಾರತೀಯ ಕೃಷಿಗಾಗಿ ವಿನ್ಯಾಸಗೊಳಿಸಿದ ಸಮಗ್ರ ಕೃಷಿ ಪರಿಹಾರಗಳು',
      farmDevelopment: {
        title: 'ಸಂಪೂರ್ಣ ಕೃಷಿ ಅಭಿವೃದ್ಧಿ',
        description: 'ನಿಮ್ಮ ಭೂಮಿಯನ್ನು ಹೆಚ್ಚಿನ ಉತ್ಪಾದಕತೆಯ ಕೃಷಿ ಆಸ್ತಿಯಾಗಿ ಪರಿವರ್ತಿಸಿ',
      },
      soilTesting: {
        title: 'ಮಣ್ಣಿನ ಪರೀಕ್ಷೆ ಮತ್ತು ಬೆಳೆ ಸಲಹೆ',
        description: 'ವೈಜ್ಞಾನಿಕ ಮಣ್ಣಿನ ವಿಶ್ಲೇಷಣೆ ಮತ್ತು ಕಸ್ಟಮೈಸ್ ಮಾಡಿದ ಬೆಳೆ ಶಿಫಾರಸುಗಳು',
      },
      precisionAgriculture: {
        title: 'ನಿಖರ ಕೃಷಿ',
        description: 'ಅತ್ಯುತ್ತಮ ಕೃಷಿ ಕಾರ್ಯಾಚರಣೆಗಳಿಗಾಗಿ ಸುಧಾರಿತ ತಂತ್ರಜ್ಞಾನ',
      },
      digitalFarming: {
        title: 'ಡಿಜಿಟಲ್ ಕೃಷಿ ಪರಿಹಾರಗಳು',
        description: 'ಆಧುನಿಕ ಕೃಷಿ ನಿರ್ವಹಣೆಗಾಗಿ ಸ್ಮಾರ್ಟ್ ಅಪ್ಲಿಕೇಶನ್‌ಗಳು ಮತ್ತು IoT ಸಾಧನಗಳು',
      },
      nursery: {
        title: 'ನರ್ಸರಿ ಮತ್ತು ತೋಟಗಾರಿಕೆ',
        description: 'ಪ್ರೀಮಿಯಂ ಗುಣಮಟ್ಟದ ಸಸಿಗಳು ಮತ್ತು ತೋಟಗಾರಿಕೆ ಪರಿಣತಿ',
      },
      irrigation: {
        title: 'ಸುಧಾರಿತ ನೀರಾವರಿ',
        description: 'ನೀರು-ದಕ್ಷ ನೀರಾವರಿ ವ್ಯವಸ್ಥೆಗಳು ಮತ್ತು ನಿರ್ವಹಣೆ',
      },
      irrigationCards: [
        {
          title: 'ಡ್ರಿಪ್ ನೀರಾವರಿ ವಿನ್ಯಾಸ',
          description: '90% ನೀರು ಪರಿಣಾಮಕಾರಿತ್ವದೊಂದಿಗೆ ಪರಿಶುದ್ಧ ನೀರಿನ ವಿತರಣೆ ವ್ಯವಸ್ಥೆ. ಪ್ರತಿಯೊಂದು ಬೆಳೆ ಮತ್ತು ಭೂಗೋಳಕ್ಕೆ ಕಸ್ಟಮೈಸ್ ಡಿಸೈನ್.',
          features: ['ನೀರು ಉಳಿವು', 'ಸಮ uniform ವಿತರಣಾ', 'ಉಗಮ ರಸಗೊಬ್ಬರ ಏಕೀಕರಣ', 'ಸ್ವಯಂಚಾಲಿತ ನಿಯಂತ್ರಣ']
        },
        {
          title: 'ಸ್ಪ್ರಿಂಕ್ಲರ್ ವ್ಯವಸ್ಥೆಗಳು',
          description: 'ವಿಸ್ತೀರ್ಣ ಪ್ರದೇಶಗಳಿಗೆ ಸಮ ವಿತರಣೆ. ಕ್ಷೇತ್ರ ಬೆಳೆ, ತರಕಾರಿಗಳು ಮತ್ತು ತೋಟಗಳಿಗಾಗಿ ಸೂಕ್ತ.',
          features: ['ವೃಹತ್ ಆವರಣೆ', 'ಅನುಕೂಲಿತ ಒತ್ತಡ', 'ಶಕ್ತಿ-ದಕ್ಷ', 'ಸರಳ ನಿರ್ವಹಣೆ']
        },
        {
          title: 'ಬೋರ್ವೆಲ್ ವಿಶ್ಲೇಷಣೆ',
          description: 'ವೈಜ್ಞಾನಿಕ ನೀರಿನ ಮೂಲ ಮೌಲ್ಯಮಾಪನ, ಗುಣಮಟ್ಟ ಪರೀಕ್ಷೆ ಮತ್ತು ಉತ್ಪಾದನೆ ಸುಧಾರಣೆ.',
          features: ['ನೀರು ಗುಣಮಟ್ಟ ಪರೀಕ್ಷೆ', 'ಫಲಿತಾಂಶ ವಿಶ್ಲೇಷಣೆ', 'ಆಳ ಶಿಫಾರಸು', 'ಸ್ಥಿರತೆ ಪರಿಶೀಲನೆ']
        },
        {
          title: 'ಮಳಿನ ನೀರು ಸಂಗ್ರಹಣೆ',
          description: 'ನೀರು ಸಂಗ್ರಹಿಸಿ ಮತ್ತು ಸುಧಾರಿತ ನೀರಾವರಿಿಗಾಗಿ ಉಪಯೋಗಿಸಿ. ನೆಲದ ನೀರಿನ ಅವಲಂಬನೆ ಕಡಿಮೆ ಮಾಡಿ.',
          features: ['ಸಂಗ್ರಹ ಪರಿಹಾರಗಳು', 'ರೀಚಾರ್ಜ್ ವ್ಯವಸ್ಥೆಗಳು', 'ಫಿಲ್ಟರ್ ವಿನ್ಯಾಸ', 'ವೆಚ್ಚ-ಫಲಪ್ರದ']
        }
      ],
      machinery: {
        title: 'ಕೃಷಿ ಯಂತ್ರೋಪಕರಣಗಳು',
        description: 'ಆಧುನಿಕ ಕೃಷಿ ಸಾಧನಗಳು ಮತ್ತು ಉಪಕರಣಗಳ ಪೂರೈಕೆ',
      },
      marketing: {
        title: 'ಮಾರುಕಟ್ಟೆ ಮತ್ತು ಕೊಯ್ಲು ನಂತರದ',
        description: 'ಮಾರುಕಟ್ಟೆ ಸಂಪರ್ಕ ಮತ್ತು ಮೌಲ್ಯವರ್ಧನೆ ಸೇವೆಗಳು',
      },
      learnMore: 'ಇನ್ನಷ್ಟು ತಿಳಿಯಿರಿ',
    },
    whyChoose: {
      title: 'ಕಲ್ಪಕೃಷಿಯನ್ನು ಏಕೆ ಆರಿಸಬೇಕು',
      subtitle: 'ಭಾರತದಲ್ಲಿ ಪ್ರಮುಖ ಕೃಷಿ ತಂತ್ರಜ್ಞಾನ ನಾವೀನ್ಯತೆ',
      expert: {
        title: 'ತಜ್ಞರ ತಂಡ',
        description: '15+ ವರ್ಷಗಳ ಕೃಷಿ ಪರಿಣತಿ',
      },
      technology: {
        title: 'ಸುಧಾರಿತ ತಂತ್ರಜ್ಞಾನ',
        description: 'ಇತ್ತೀಚಿನ IoT ಮತ್ತು AI ಪರಿಹಾರಗಳು',
      },
      support: {
        title: '24/7 ಬೆಂಬಲ',
        description: 'ಗಡಿಯಾರದ ಸುತ್ತಿನ ರೈತ ಸಹಾಯ',
      },
      proven: {
        title: 'ಸಾಬೀತಾದ ಫಲಿತಾಂಶಗಳು',
        description: '500+ ಯಶಸ್ವಿ ಯೋಜನೆಗಳು',
      },
    },
    projects: {
      title: 'ನಮ್ಮ ಯಶೋಗಾಥೆಗಳು',
      subtitle: 'ಭಾರತದಾದ್ಯಂತ ನಿಜವಾದ ಪರಿವರ್ತನೆಗಳು',
      viewProject: 'ವಿವರಗಳನ್ನು ವೀಕ್ಷಿಸಿ',
      list: [
        {
          title: 'ಮಿಶ್ರ ಕೃಷಿ ವಿನ್ಯಾಸ - 50 ಎಕರೆ',
          location: 'ಕರ್ನಾಟಕ',
          type: 'ಕೃಷಿ ಅಭಿವೃದ್ಧಿ'
        },
        {
          title: 'ಡ್ರಿಪ್ ನೀರಾವರಿ ವ್ಯವಸ್ಥೆ ವಿನ್ಯಾಸ',
          location: 'ತಿಪಟೂರು ಪ್ರದೇಶ',
          type: 'ನೀರಾವರಿ'
        },
        {
          title: 'ವಾಣಿಜ್ಯಿಕ ಪಾಲಿಹೌಸ್ ಸ್ಥಾಪನೆ',
          location: 'ಹಸನ್ ಜಿಲ್ಲೆ',
          type: 'ಮೂಲಸೌಕರ್ಯ'
        },
        {
          title: 'ನಿಖರ ಕೃಷಿ - ಡ್ರೋನ್ ಸೇವೆಗಳು',
          location: 'ಅನೇಕ ಸ್ಥಳಗಳು',
          type: 'ತಂತ್ರಜ್ಞಾನ'
        },
        {
          title: 'ಮಣ್ಣು ಪರೀಕ್ಷೆ ಮತ್ತು ಸಲಹೆ ಕಾರ್ಯಕ್ರಮ',
          location: 'ತುಮಕೂರು ಪ್ರದೇಶ',
          type: 'ಸಲಹೆ'
        },
        {
          title: 'ಆಧುನಿಕ ಸಾಧನಗಳ ಅನುಷ್ಠಾನ',
          location: 'ಶರದಾ ನಗರ, ತಿಪಟೂರು',
          type: 'ಸಾಧನ'
        }
      ]
    },
    about: {
      title: 'ಕಲ್ಪಕೃಷಿ ಬಗ್ಗೆ',
      subtitle: 'ಸುಸ್ಥಿರ ಕೃಷಿಯನ್ನು ಪ್ರವರ್ತಕವಾಗಿಸುವುದು',
      description: 'ಕಲ್ಪಕೃಷಿಯು ನವೀನ ತಂತ್ರಜ್ಞಾನ ಮತ್ತು ಸುಸ್ಥಿರ ಅಭ್ಯಾಸಗಳ ಮೂಲಕ ಭಾರತೀಯ ಕೃಷಿಯನ್ನು ಕ್ರಾಂತಿಗೊಳಿಸುತ್ತಿದೆ. ನಾವು ಹೆಚ್ಚಿನ ಉತ್ಪಾದಕತೆ ಮತ್ತು ಲಾಭದಾಯಕತೆಗಾಗಿ ಅತ್ಯಾಧುನಿಕ ಪರಿಹಾರಗಳೊಂದಿಗೆ ರೈತರಿಗೆ ಅಧಿಕಾರ ನೀಡುತ್ತೇವೆ.',
      mission: {
        title: 'ನಮ್ಮ ಧ್ಯೇಯ',
        text: 'ಭಾರತೀಯ ಕೃಷಿಯನ್ನು ಆಧುನಿಕೀಕರಣಗೊಳಿಸಲು ಅತ್ಯಾಧುನಿಕ ತಂತ್ರಜ್ಞಾನ, ತಜ್ಞ ಮಾರ್ಗದರ್ಶನ ಮತ್ತು ಸುಸ್ಥಿರ ಕೃಷಿ ಪರಿಹಾರಗಳನ್ನು ಒದಗಿಸುವುದು.'
      },
      vision: {
        title: 'ನಮ್ಮ ದೃಷ್ಟಿ',
        text: 'ಸ್ಮಾರ್ಟ್, ಲಾಭದಾಯಕ ಮತ್ತು ಸುಸ್ಥಿರ ಕೃಷಿ පರಿಸರಗಳನ್ನು ನಿರ್ಮಿಸುವಲ್ಲಿ ಭಾರತದ ವಿಶ್ವಾಸಾರ್ಹ AgriTech ಪಾಲುದಾರರಾಗುವುದು.'
      },
      milestones: [
        { year: '2024', title: 'ನಿಗಮ ಸ್ಥಾಪನೆ', desc: 'ಕೃಷಿಯನ್ನು ಆಧುನಿಕೀಕರಿಸುವ ದೃಷ್ಟಿಯಿಂದ ಪ್ರಾರಂಭ' },
        { year: '2024', title: '50+ ಕೃಷಿಭೂಮಿ', desc: 'ಯಶಸ್ವಿಯಾಗಿ ಅಭಿವೃದ್ಧಿಗೊಂಡ ಕೃಷಿಭೂಮಿ' },
        { year: '2024', title: '150+ ಎಕರೆ', desc: 'ವೃತ್ತಿಪರ ನಿರ್ವಹಣೆಯಡಿ ಭೂಮಿ' },
        { year: '2025', title: '50+ ಕೃಷಿಭೂಮಿ', desc: 'ಕೃಷಿ ಅಭಿವೃದ್ಧಿಯಲ್ಲಿ ಮೈಲಿಗಲ್ಲು' }
      ],
      achievements: [
        { title: '150+ ಎಕರೆ', desc: 'ವೃತ್ತಿಪರ ನಿರ್ವಹಣೆಯಲ್ಲಿ' },
        { title: '50+ ಫಾರ್ಮುಗಳು', desc: 'ಯಶಸ್ವಿಯಾಗಿ ಅಭಿವೃದ್ಧಿಪಡಿಸಲಾಗಿದೆ' },
        { title: '2024-2025', desc: 'ವೇಗವಾಗಿ ವಿಸ್ತರಣೆ' }
      ],
      stats: {
        projects: 'ಯೋಜನೆಗಳು ಪೂರ್ಣಗೊಂಡಿವೆ',
        farmers: 'ರೈತರಿಗೆ ಸಹಾಯ ಮಾಡಲಾಗಿದೆ',
        hectares: 'ಹೆಕ್ಟೇರ್‌ಗಳು ಪರಿವರ್ತನೆಗೊಂಡಿವೆ',
        states: 'ರಾಜ್ಯಗಳನ್ನು ಒಳಗೊಂಡಿದೆ',
      },
    },
    irrigation: {
      title: 'ಸುಧಾರಿತ ನೀರಾವರಿ ಪರಿಹಾರಗಳು',
      subtitle: 'ನೀರು-ದಕ್ಷ ಕೃಷಿ ವ್ಯವಸ್ಥೆಗಳು',
      drip: 'ಹನಿ ನೀರಾವರಿ',
      sprinkler: 'ಸ್ಪ್ರಿಂಕ್ಲರ್ ವ್ಯವಸ್ಥೆಗಳು',
      automation: 'ಸ್ಮಾರ್ಟ್ ಆಟೊಮೇಷನ್',
      getQuote: 'ಉಲ್ಲೇಖವನ್ನು ಪಡೆಯಿರಿ',
    },
    digitalApp: {
      title: 'ಕಲ್ಪಕೃಷಿ ಡಿಜಿಟಲ್ ಅಪ್ಲಿಕೇಶನ್',
      subtitle: 'ನಿಮ್ಮ ಬೆರಳ ತುದಿಯಲ್ಲಿ ಕೃಷಿ ನಿರ್ವಹಣೆ',
      monitoring: 'ನೈಜ-ಸಮಯದ ಮೇಲ್ವಿಚಾರಣೆ',
      weather: 'ಹವಾಮಾನ ಮುನ್ಸೂಚನೆಗಳು',
      advisory: 'ತಜ್ಞರ ಸಲಹೆ',
      marketplace: 'ಮಾರುಕಟ್ಟೆ',
      download: 'ಈಗ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ',
    },
    globalServices: {
      title: 'ಜಾಗತಿಕ ಸೇವೆಗಳು',
      subtitle: 'ನಾವು ವಿಶ್ವಮಟ್ಟದ ಕೃಷಿ ಪರಿಹಾರಗಳನ್ನು ಒದಗಿಸುತ್ತೇವೆ',
      list: [
        { title: 'ವಿಶ್ವಮಟ್ಟದ ಸೇವೆ', description: 'ಪ್ರದೇಶಗಳಾದ್ಯಂತ ಪ್ರೋಫೆಷನಲ್ ಕೃಷಿ ಸೇವೆಗಳು.' },
        { title: 'ಪ್ರಶಸ್ತಿ ಪಡೆದ ಗುಣಮಟ್ಟ', description: 'ನವೀನ ಮತ್ತು ಸುಸ್ಥಿರ ಅಭ್ಯಾಸಗಳಿಗಾಗಿ ಗುರುತಿಸಿಕೊಂಡಿದೆ.' },
        { title: 'ಗ್ರಾಹಕ ಕೇಂದ್ರಿತ', description: 'ನಿಮ್ಮ ತೃಪ್ತಿಯೇ ನಮ್ಮ ಮೊದಲ ಆದ್ಯತೆ.' }
      ]
    },
    testimonials: {
      title: 'ರೈತರು ಏನು ಹೇಳುತ್ತಾರೆ',
      subtitle: 'ಭಾರತದಾದ್ಯಂತ ಸಾವಿರಾರು ಜನರು ನಂಬುತ್ತಾರೆ',
      list: [
        { name: 'ರಾಜೇಶ್ ಕುಮಾರ್', role: 'ರೈತ - 30 ಎಕರೆ', location: 'ಟಿಪ್ಟೂರ್, ಕರ್ನಾಟಕ', text: 'Kalpakrushi ನನ್ನ ರೈತದ್ದನ್ನು ಪರಿವರ್ತಿಸಿದೆ. ಡ್ರಿಪ್ ಸಿಂಚನದಿಂದ ನೀರು ಉಳಿಯಿತು ಮತ್ತು ಆದಾಯ ಹೆಚ್ಚಾಯಿತು.' },
        { name: 'ಮಂಜುನಾಥ್ ಗೌಡ', role: 'ವ್ಯಾಪಾರಿಕ ರೈತ', location: 'ಹಾಸನ್ ಜಿಲ್ಲೆ', text: 'ಡ್ರೋನ್ ಸೇವೆಗಳು ಮತ್ತು ನಿಖರ ಕೃಷಿ ನಮ್ಮ ಕಾರ್ಯಕ್ಷಮತೆಯನ್ನು ಹೆಚ್ಚಿಸಿದವು.' },
        { name: 'ಲಕ್ಷ್ಮಿ ದೇವಿ', role: 'ಆರ್ಗ್ಯಾನಿಕ್ ಫಾರ್ಮ್ ಮಾಲಕಿ', location: 'ತುಮಕೂರು', text: 'Kalpakrushi ನಮ್ಮ ಸಸ್ಯೋತ್ಪಾದನಾ ಅಗತ್ಯಗಳಿಗೆ ಸುಸ್ಥಿರ ಪರಿಹಾರಗಳನ್ನು ಒದಗಿಸಿತು.' },
        { name: 'ಸುರೇಶ್ ರೆಡ್ಡಿ', role: 'ಬಹು-ಬೆಳೆ ರೈತ', location: 'ಶರದಾ ನಗರ', text: 'ಸಬ್ಸಿಡಿ ಮಾರ್ಗದರ್ಶನ ಮತ್ತು ಪಾಲಿಹೌಸ್ ಸೆಟ್‌ಅಪ್ ನನ್ನ ಆದಾಯವನ್ನು ಹೆಚ್ಚಿಸಿದೆ.' }
      ]
    },
    contact: {
      title: 'ಸಂಪರ್ಕದಲ್ಲಿರಿ',
      subtitle: 'ನಿಮ್ಮ ಜಮೀನನ್ನು ಪರಿವರ್ತಿಸಲು ಸಿದ್ಧರಿದ್ದೀರಾ? ಉಚಿತ ಸಮಾಲೋಚನೆ ಮತ್ತು ಕಸ್ಟಮ್ ಕೃಷಿ ಪರಿಹಾರಗಳಿಗಾಗಿ ಇಂದೇ ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ',
      form: {
        name: 'ನಿಮ್ಮ ಹೆಸರು',
        email: 'ಇಮೇಲ್ ವಿಳಾಸ',
        phone: 'ದೂರವಾಣಿ ಸಂಖ್ಯೆ',
        location: 'ಫಾರ್ಮ್ ಸ್ಥಳ',
        message: 'ನಿಮ್ಮ ಸಂದೇಶ',
        submit: 'ಸಂದೇಶವನ್ನು ಕಳುಹಿಸಿ',
      },
      callUs: 'ನಮಗೆ ಕರೆ ಮಾಡಿ',
      emailUs: 'ಇಮೇಲ್ ಮಾಡಿ',
      visitUs: 'ನಮ್ಮನ್ನು ಭೇಟಿ ಮಾಡಿ',
      businessHours: 'ವ್ಯವಹಾರ ಸಮಯ',
      address: 'ಶಾರದಾ ನಗರ, ಟಿಪ್ಟೂರು, ಕರ್ನಾಟಕ',
    },
    footer: {
      description: 'ಸ್ಮಾರ್ಟ್ ತಂತ್ರಜ್ಞಾನ ಮತ್ತು ಸುಸ್ಥಿರ ಕೃಷಿ ಪರಿಹಾರಗಳೊಂದಿಗೆ ಭಾರತೀಯ ಕೃಷಿಯನ್ನು ಪರಿವರ್ತಿಸುವುದು.',
      quickLinks: 'ತ್ವರಿತ ಲಿಂಕ್‌ಗಳು',
      services: 'ನಮ್ಮ ಸೇವೆಗಳು',
      contactInfo: 'ಸಂಪರ್ಕ ಮಾಹಿತಿ',
      copyright: '© 2025 ಕಲ್ಪಕೃಷಿ ಕೃಷಿ ತಂತ್ರಜ್ಞಾನ. ಎಲ್ಲಾ ಹಕ್ಕುಗಳನ್ನು ಕಾಯ್ದಿರಿಸಲಾಗಿದೆ.',
      privacy: 'ಗೌಪ್ಯತಾ ನೀತಿ',
      terms: 'ಸೇವಾ ನಿಯಮಗಳು',
      sitemap: 'ಸೈಟ್‌ಮ್ಯಾಪ್',
    },
    common: {
      readMore: 'ಇನ್ನಷ್ಟು ಓದಿ',
      getStarted: 'ಪ್ರಾರಂಭಿಸಿ',
      contactUs: 'ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ',
      viewAll: 'ಎಲ್ಲವನ್ನೂ ವೀಕ್ಷಿಸಿ',
    },
  },
  hi: {
    nav: {
      home: 'होम',
      services: 'सेवाएं',
      about: 'हमारे बारे में',
      projects: 'परियोजनाएं',
      contact: 'संपर्क करें',
    },
    hero: {
      title: 'अपने खेत को बदलें',
      titleHighlight: 'स्मार्ट कृषि के साथ',
      subtitle: 'अत्याधुनिक तकनीक, स्थायी प्रथाओं और अधिकतम उपज के लिए विशेषज्ञ मार्गदर्शन के साथ भारतीय कृषि में क्रांति',
      cta: 'शुरू करें',
      ctaSecondary: 'और जानें',
    },
    services: {
      title: 'हमारी प्रीमियम सेवाएं',
      subtitle: 'आधुनिक भारतीय कृषि के लिए व्यापक कृषि समाधान',
      farmDevelopment: {
        title: 'संपूर्ण कृषि विकास',
        description: 'अपनी भूमि को उच्च उत्पादकता वाली कृषि संपत्ति में बदलें',
      },
      soilTesting: {
        title: 'मिट्टी परीक्षण और फसल सलाह',
        description: 'वैज्ञानिक मिट्टी विश्लेषण और अनुकूलित फसल सिफारिशें',
      },
      precisionAgriculture: {
        title: 'सटीक कृषि',
        description: 'अनुकूलित कृषि संचालन के लिए उन्नत तकनीक',
      },
      digitalFarming: {
        title: 'डिजिटल कृषि समाधान',
        description: 'आधुनिक कृषि प्रबंधन के लिए स्मार्ट ऐप्स और IoT उपकरण',
      },
      nursery: {
        title: 'नर्सरी और बागान',
        description: 'प्रीमियम गुणवत्ता वाले पौधे और बागान विशेषज्ञता',
      },
      irrigation: {
        title: 'उन्नत सिंचाई',
        description: 'जल-कुशल सिंचाई प्रणाली और प्रबंधन',
      },
      machinery: {
        title: 'कृषि मशीनरी और उपकरण',
        description: 'आधुनिक कृषि उपकरण और उपकरणों की आपूर्ति',
      },
      marketing: {
        title: 'विपणन और कटाई के बाद',
        description: 'बाजार संपर्क और मूल्य वर्धन सेवाएं',
      },
      learnMore: 'और जानें',
    },
    whyChoose: {
      title: 'कल्पकृषि क्यों चुनें',
      subtitle: 'भारत में अग्रणी कृषि प्रौद्योगिकी नवाचार',
      expert: {
        title: 'विशेषज्ञ टीम',
        description: '15+ वर्षों की कृषि विशेषज्ञता',
      },
      technology: {
        title: 'उन्नत तकनीक',
        description: 'नवीनतम IoT और AI समाधान',
      },
      support: {
        title: '24/7 सहायता',
        description: 'चौबीसों घंटे किसान सहायता',
      },
      proven: {
        title: 'सिद्ध परिणाम',
        description: '500+ सफल परियोजनाएं',
      },
    },
    projects: {
      title: 'हमारी सफलता की कहानियां',
      subtitle: 'भारत भर में वास्तविक परिवर्तन',
      viewProject: 'विवरण देखें',
      list: [
        {
          title: 'मिश्रित कृषि लेआउट - 50 एकड़',
          location: 'कर्नाटक',
          type: 'कृषि विकास'
        },
        {
          title: 'ड्रिप सिंचाई प्रणाली डिजाइन',
          location: 'तिपटूर क्षेत्र',
          type: 'सिंचाई'
        },
        {
          title: 'व्यावसायिक पॉलीहाउस सेटअप',
          location: 'हासन जिला',
          type: 'बुनियादी ढांचा'
        },
        {
          title: 'सटीक कृषि - ड्रोन सेवाएं',
          location: 'एकाधिक स्थान',
          type: 'प्रौद्योगिकी'
        },
        {
          title: 'मिट्टी परीक्षण और सलाहकार कार्यक्रम',
          location: 'तुमकुर क्षेत्र',
          type: 'सलाहकार'
        },
        {
          title: 'आधुनिक उपकरण कार्यान्वयन',
          location: 'शरदा नगर, तिपटूर',
          type: 'उपकरण'
        }
      ]
    },
    about: {
      title: 'कल्पकृषि के बारे में',
      subtitle: 'स्थायी कृषि का मार्ग प्रशस्त करना',
      description: 'कल्पकृषि नवीन तकनीक और स्थायी प्रथाओं के माध्यम से भारतीय कृषि में क्रांति ला रहा है। हम बढ़ी हुई उत्पादकता और लाभप्रदता के लिए अत्याधुनिक समाधानों के साथ किसानों को सशक्त बनाते हैं।',
      mission: {
        title: 'हमारा मिशन',
        text: 'भारतीय कृषि को आधुनिकीकृत करने के लिए अत्याधुनिक तकनीक, विशेषज्ञ मार्गदर्शन और स्थायी कृषि समाधान प्रदान करना।'
      },
      vision: {
        title: 'हमारी दृष्टि',
        text: 'स्मार्ट, लाभदायक और टिकाऊ कृषि पारिस्थितिकी तंत्र बनाने में भारत का विश्वसनीय कृषि प्रौद्योगिकी भागीदार बनना।'
      },
      stats: {
        projects: 'परियोजनाएं पूर्ण',
        farmers: 'किसानों की मदद की',
        hectares: 'हेक्टेयर रूपांतरित',
        states: 'राज्य शामिल',
      },
    },
    irrigation: {
      title: 'उन्नत सिंचाई समाधान',
      subtitle: 'जल-कुशल कृषि प्रणाली',
      drip: 'ड्रिप सिंचाई',
      sprinkler: 'स्प्रिंकलर सिस्टम',
      automation: 'स्मार्ट ऑटोमेशन',
      getQuote: 'कोटेशन प्राप्त करें',
    },
    digitalApp: {
      title: 'कल्पकृषि डिजिटल ऐप',
      subtitle: 'आपकी उंगलियों पर कृषि प्रबंधन',
      monitoring: 'रीयल-टाइम निगरानी',
      weather: 'मौसम पूर्वानुमान',
      advisory: 'विशेषज्ञ सलाह',
      marketplace: 'बाज़ार',
      download: 'अभी डाउनलोड करें',
    },
    globalServices: {
      title: 'वैश्विक सेवाएँ',
      subtitle: 'हम विश्व-स्तरीय कृषि समाधान प्रदान करते हैं',
      list: [
        { title: 'वैश्विक सेवा', description: 'हम कई क्षेत्रों में पेशेवर कृषि सेवाएँ प्रदान करते हैं।' },
        { title: 'पुरस्कार विजेता गुणवत्ता', description: 'नवोन्मेषी और टिकाऊ प्रथाओं के लिए मान्यता प्राप्त।' },
        { title: 'ग्राहक-केंद्रित', description: 'आपकी संतुष्टि हमारी प्राथमिकता है।' }
      ]
    },
    testimonials: {
      title: 'किसान क्या कहते हैं',
      subtitle: 'भारत भर में हजारों लोगों द्वारा विश्वसनीय',
      list: [
        { name: 'राजेश कुमार', role: 'किसान - 30 एकड़', location: 'टिप्टूर, कर्नाटक', text: 'Kalpakrushi ने मेरे खेत को बदल दिया। ड्रिप इरिगेशन ने पानी बचाया और फसल बढ़ी।' },
        { name: 'मंजूनाथ गौड़ा', role: 'व्यावसायिक किसान', location: 'हासन जिला', text: 'प्रिसिजन एग्रीकल्चर और ड्रोन सेवाओं ने हमारे काम को नया रूप दिया।' },
        { name: 'लक्ष्मी देवी', role: 'ऑर्गेनिक फार्म मालिक', location: 'तुमकुर', text: 'Kalpakrushi ने टिकाऊ समाधानों के साथ हमारी खेती में मदद की।' },
        { name: 'सुरेश रेड्डी', role: 'बहु-फसल किसान', location: 'शारदा नगर', text: 'सब्सिडी मार्गदर्शन और पॉलीहाउस सेटअप ने मेरी आय बढ़ाई।' }
      ]
    },
    contact: {
      title: 'संपर्क में रहें',
      subtitle: 'अपने खेत को बदलने के लिए तैयार हैं? मुफ्त परामर्श और कस्टम कृषि समाधान के लिए आज ही हमसे संपर्क करें',
      form: {
        name: 'आपका नाम',
        email: 'ईमेल पता',
        phone: 'फोन नंबर',
        location: 'फार्म स्थान',
        message: 'आपका संदेश',
        submit: 'संदेश भेजें',
      },
      callUs: 'हमें कॉल करें',
      emailUs: 'हमें ईमेल करें',
      visitUs: 'हमसे मिलें',
      businessHours: 'कार्य समय',
      address: 'शारदा नगर, टिप्टूर, कर्नाटक',
    },
    footer: {
      description: 'स्मार्ट तकनीक और स्थायी कृषि समाधानों के साथ भारतीय कृषि को बदलना।',
      quickLinks: 'त्वरित लिंक',
      services: 'हमारी सेवाएं',
      contactInfo: 'संपर्क जानकारी',
      copyright: '© 2025 कल्पकृषि कृषि प्रौद्योगिकी। सर्वाधिकार सुरक्षित।',
      privacy: 'गोपनीयता नीति',
      terms: 'सेवा की शर्तें',
      sitemap: 'साइटमैप',
    },
    common: {
      readMore: 'और पढ़ें',
      getStarted: 'शुरू करें',
      contactUs: 'हमसे संपर्क करें',
      viewAll: 'सभी देखें',
    },
  },
  ta: {
    nav: {
      home: 'முகப்பு',
      services: 'சேவைகள்',
      about: 'எங்களை பற்றி',
      projects: 'திட்டங்கள்',
      contact: 'தொடர்பு',
    },
    hero: {
      title: 'உங்கள் பண்ணையை மாற்றுங்கள்',
      titleHighlight: 'ஸ்மார்ட் விவசாயத்துடன்',
      subtitle: 'அதிநவீன தொழில்நுட்பம், நிலையான நடைமுறைகள் மற்றும் அதிகபட்ச விளைச்சலுக்கான நிபுணர் வழிகாட்டுதலுடன் இந்திய விவசாயத்தில் புரட்சி',
      cta: 'தொடங்குங்கள்',
      ctaSecondary: 'மேலும் அறிக',
    },
    services: {
      title: 'எங்கள் பிரீமியம் சேவைகள்',
      subtitle: 'நவீன இந்திய விவசாயத்திற்கான விரிவான விவசாய தீர்வுகள்',
      farmDevelopment: {
        title: 'முழுமையான பண்ணை மேம்பாடு',
        description: 'உங்கள் நிலத்தை அதிக உற்பத்தித்திறன் கொண்ட விவசாய சொத்தாக மாற்றுங்கள்',
      },
      soilTesting: {
        title: 'மண் பரிசோதனை மற்றும் பயிர் ஆலோசனை',
        description: 'அறிவியல் மண் பகுப்பாய்வு மற்றும் தனிப்பயனாக்கப்பட்ட பயிர் பரிந்துரைகள்',
      },
      precisionAgriculture: {
        title: 'துல்லிய விவசாயம்',
        description: 'மேம்படுத்தப்பட்ட விவசாய செயல்பாடுகளுக்கான மேம்பட்ட தொழில்நுட்பம்',
      },
      digitalFarming: {
        title: 'டிஜிட்டல் விவசாய தீர்வுகள்',
        description: 'நவீன பண்ணை நிர்வாகத்திற்கான ஸ்மார்ட் பயன்பாடுகள் மற்றும் IoT சாதனங்கள்',
      },
      nursery: {
        title: 'நர்சரி மற்றும் தோட்டம்',
        description: 'பிரீமியம் தரமான நாற்றுகள் மற்றும் தோட்டக்கலை நிபுணத்துவம்',
      },
      irrigation: {
        title: 'மேம்பட்ட நீர்ப்பாசனம்',
        description: 'நீர்-திறனுள்ள நீர்ப்பாசன அமைப்புகள் மற்றும் நிர்வாகம்',
      },
      machinery: {
        title: 'பண்ணை இயந்திரங்கள் மற்றும் உபகரணங்கள்',
        description: 'நவீன விவசாய கருவிகள் மற்றும் உபகரண விநியோகம்',
      },
      marketing: {
        title: 'சந்தைப்படுத்தல் மற்றும் அறுவடைக்கு பிந்தைய',
        description: 'சந்தை இணைப்பு மற்றும் மதிப்பு கூட்டல் சேவைகள்',
      },
      learnMore: 'மேலும் அறிக',
    },
    whyChoose: {
      title: 'கல்பக்ருஷியை ஏன் தேர்வு செய்ய வேண்டும்',
      subtitle: 'இந்தியாவில் முன்னணி விவசாய தொழில்நுட்ப புதுமை',
      expert: {
        title: 'நிபுணர் குழு',
        description: '15+ ஆண்டுகள் விவசாய நிபுணத்துவம்',
      },
      technology: {
        title: 'மேம்பட்ட தொழில்நுட்பம்',
        description: 'சமீபத்திய IoT மற்றும் AI தீர்வுகள்',
      },
      support: {
        title: '24/7 ஆதரவு',
        description: 'வட்டமான விவசாயி உதவி',
      },
      proven: {
        title: 'நிரூபிக்கப்பட்ட முடிவுகள்',
        description: '500+ வெற்றிகரமான திட்டங்கள்',
      },
    },
    projects: {
      title: 'எங்கள் வெற்றி கதைகள்',
      subtitle: 'இந்தியா முழுவதும் உண்மையான மாற்றங்கள்',
      viewProject: 'விவரங்களைப் பார்க்கவும்',
      list: [
        {
          title: 'கலப்பு விவசாய அமைப்பு - 50 ஏக்கர்',
          location: 'கர்நாடகா',
          type: 'விவசாய வளர்ச்சி'
        },
        {
          title: 'சொட்டு நீர்ப்பாசன அமைப்பு வடிவமைப்பு',
          location: 'டிப்டூர் பகுதி',
          type: 'நீர்ப்பாசனம்'
        },
        {
          title: 'வணிக பாலிஹவுஸ் அமைப்பு',
          location: 'ஹாசன் மாவட்டம்',
          type: 'அடிப்படை வசதிகள்'
        },
        {
          title: 'துல்லிய விவசாயம் - ட்ரோன் சேவைகள்',
          location: 'பல இடங்கள்',
          type: 'தொழில்நுட்பம்'
        },
        {
          title: 'மண் பரிசோதனை மற்றும் ஆலோசனை திட்டம்',
          location: 'தும்கூர் பகுதி',
          type: 'ஆலோசனை'
        },
        {
          title: 'நவீன உபகரணங்கள் செயல்படுத்தல்',
          location: 'ஷாரதா நகர், டிப்டூர்',
          type: 'உபகரணங்கள்'
        }
      ]
    },
    about: {
      title: 'கல்பக்ருஷி பற்றி',
      subtitle: 'நிலையான விவசாயத்தை முன்னோடியாக்குதல்',
      description: 'கல்பக்ருஷி புதுமையான தொழில்நுட்பம் மற்றும் நிலையான நடைமுறைகள் மூலம் இந்திய விவசாயத்தில் புரட்சி செய்து வருகிறது. மேம்பட்ட உற்பத்தித்திறன் மற்றும் லாபத்தன்மைக்கான அதிநவீன தீர்வுகளுடன் விவசாயிகளுக்கு நாங்கள் அதிகாரம் அளிக்கிறோம்.',
      mission: {
        title: 'எங்கள் நோக்கம்',
        text: 'இந்திய விவசாயத்தை আধুनिकப்படுத்த அত்याধுনிக தொழில்நுட்பம், நிபுணர் வழிகாட்டுதல் மற்றும் நிலையான விவசாய தீர்வுகளை வழங்குவது.'
      },
      vision: {
        title: 'எங்கள் দৃष்டிக்கோணம்',
        text: 'புத்திசாலி, லாभवान மற்றும் நிலையான விவசாய சூழல்களை உருவாக்குவதில் இந்தியாவின் நம்பகமான கૃषि தொழில்நுட்ப பங்குதாரர் ஆவது.'
      },
      stats: {
        projects: 'திட்டங்கள் நிறைவடைந்தன',
        farmers: 'விவசாயிகளுக்கு உதவினோம்',
        hectares: 'ஹெक்டேர்கள் மாற்றப்பட்டன',
        states: 'மாநிலங்கள் உள்ளடக்கப்பட்டன',
      },
    },
    irrigation: {
      title: 'மேம்பட்ட நீர்ப்பாசன தீர்வுகள்',
      subtitle: 'நீர்-திறனுள்ள விவசாய அமைப்புகள்',
      drip: 'சொட்டு நீர்ப்பாசனம்',
      sprinkler: 'தெளிப்பான் அமைப்புகள்',
      automation: 'ஸ்மார்ட் ஆட்டோமேஷன்',
      getQuote: 'மேற்கோள் பெறுங்கள்',
    },
    digitalApp: {
      title: 'கல்பக்ருஷி டிஜிட்டல் பயன்பாடு',
      subtitle: 'உங்கள் விரல் நுனியில் பண்ணை நிர்வாகம்',
      monitoring: 'நிகழ்நேர கண்காணிப்பு',
      weather: 'வானிலை முன்னறிவிப்புகள்',
      advisory: 'நிபுணர் ஆலோசனை',
      marketplace: 'சந்தை',
      download: 'இப்போது பதிவிறக்கவும்',
    },
    globalServices: {
      title: 'உலகளாவிய சேவைகள்',
      subtitle: 'நாங்கள் உலக தரமான விவசாய தீர்வுகளை வழங்குகிறோம்',
      list: [
        { title: 'உலகளாவிய சேவை', description: 'பல பகுதிகளில் தொழில்முனைவோர் விவசாய சேவைகள் வழங்குகிறோம்.' },
        { title: 'விருதைப் பெற்ற தரம்', description: 'புதுமையான மற்றும் நிலையான நடைமுறைகளுக்காக அங்கீகாரம் பெற்றது.' },
        { title: 'வாடிக்கையாளர் மையமாக்கப்பட்டவை', description: 'உங்கள் திருப்தி தான் எங்கள் முன்னுரிமை.' }
      ]
    },
    testimonials: {
      title: 'விவசாயிகள் என்ன கூறுகிறார்கள்',
      subtitle: 'இந்தியா முழுவதும் ஆயிரக்கணக்கானோர் நம்புகிறார்கள்',
      list: [
        { name: 'ராஜேஷ் குமார்', role: 'விவசாயி - 30 ஏക്കர்', location: 'டிப்டூர், கர்நாடகா', text: 'Kalpakrushi என் பண்ணையை முற்றிலும் மாற்றியது. ட்ரிப் ஈரி காப்பு நீர் சேமித்தது மற்றும் விளைச்சல் உயர்ந்தது.' },
        { name: 'மஞ்ஜுனாத் கவுடா', role: 'வணிக விவசாயி', location: 'ஹசன் மாவட்டம்', text: 'ட்ரோன் சேவைகள் மற்றும் துல்லிய விவசாயம் எங்கள் செயல்திறனை உயர்த்தின.' },
        { name: 'லட்சுமி தேவீ', role: 'ஆர்கானிக் பண்ணை உரிமையாளர்', location: 'தும்கூர்', text: 'Kalpakrushi நமது பண்ணைக்கு நிலையான தீர்வுகளை வழங்கியது.' },
        { name: 'சுரேஷ் ரெட்டி', role: 'பல-பயிர் விவசாயி', location: 'ஷாரதா நகர்', text: 'திப்திகள் மற்றும் பாலிஹவுஸ் அமைப்பு என் வருமானத்தை அதிகப்படுத்தியது.' }
      ]
    },
    contact: {
      title: 'தொடர்பில் இருங்கள்',
      subtitle: 'உங்கள் பண்ணையை மாற்ற தயாரா? இலவச ஆலோசனை மற்றும் தனிப்பயன் விவசாய தீர்வுகளுக்கு இன்றே எங்களை தொடர்பு கொள்ளுங்கள்',
      form: {
        name: 'உங்கள் பெயர்',
        email: 'மின்னஞ்சல் முகவரி',
        phone: 'தொலைபேசி எண்',
        location: 'பண்ணை இடம்',
        message: 'உங்கள் செய்தி',
        submit: 'செய்தியை அனுப்பவும்',
      },
      callUs: 'எங்களை அழைக்கவும்',
      emailUs: 'எங்களுக்கு மின்னஞ்சல் செய்யவும்',
      visitUs: 'எங்களை பார்வையிடவும்',
      businessHours: 'வணிக நேரங்கள்',
      address: 'ஷாரதா நகர், டிப்டூர், கர்நாடகா',
    },
    footer: {
      description: 'ஸ்மார்ட் தொழில்நுட்பம் மற்றும் நிலையான விவசாய தீர்வுகளுடன் இந்திய விவசாயத்தை மாற்றுதல்.',
      quickLinks: 'விரைவு இணைப்புகள்',
      services: 'எங்கள் சேவைகள்',
      contactInfo: 'தொடர்பு தகவல்',
      copyright: '© 2025 கல்பக்ருஷி விவசாய தொழில்நுட்பம். அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.',
      privacy: 'தனியுரிமை கொள்கை',
      terms: 'சேவை விதிமுறைகள்',
      sitemap: 'தள வரைபடம்',
    },
    common: {
      readMore: 'மேலும் படிக்கவும்',
      getStarted: 'தொடங்குங்கள்',
      contactUs: 'எங்களை தொடர்பு கொள்ளுங்கள்',
      viewAll: 'அனைத்தையும் பார்க்கவும்',
    },
  },
  te: {
    nav: {
      home: 'హోమ్',
      services: 'సేవలు',
      about: 'మా గురించి',
      projects: 'ప్రాజెక్టులు',
      contact: 'సంప్రదించండి',
    },
    hero: {
      title: 'మీ పొలాన్ని మార్చండి',
      titleHighlight: 'స్మార్ట్ వ్యవసాయంతో',
      subtitle: 'అత్యాధునిక సాంకేతికత, స్థిరమైన పద్ధతులు మరియు గరిష్ట దిగుబడి కోసం నిపుణుల మార్గదర్శకత్వంతో భారతీయ వ్యవసాయంలో విప్లవం',
      cta: 'ప్రారంభించండి',
      ctaSecondary: 'మరింత తెలుసుకోండి',
    },
    services: {
      title: 'మా ప్రీమియం సేవలు',
      subtitle: 'ఆధునిక భారతీయ వ్యవసాయం కోసం సమగ్ర వ్యవసాయ పరిష్కారాలు',
      farmDevelopment: {
        title: 'పూర్తి పొలం అభివృద్ధి',
        description: 'మీ భూమిని అధిక ఉత్పాదకత గల వ్యవసాయ ఆస్తిగా మార్చండి',
      },
      soilTesting: {
        title: 'నేల పరీక్ష మరియు పంట సలహా',
        description: 'శాస్త్రీయ నేల విశ్లేషణ మరియు అనుకూలీకరించిన పంట సిఫార్సులు',
      },
      precisionAgriculture: {
        title: 'ఖచ్చితమైన వ్యవసాయం',
        description: 'ఆప్టిమైజ్ చేయబడిన వ్యవసాయ కార్యకలాపాల కోసం అధునాతన సాంకేతికత',
      },
      digitalFarming: {
        title: 'డిజిటల్ వ్యవసాయ పరిష్కారాలు',
        description: 'ఆధునిక పొలం నిర్వహణ కోసం స్మార్ట్ యాప్‌లు మరియు IoT పరికరాలు',
      },
      nursery: {
        title: 'నర్సరీ మరియు తోటలు',
        description: 'ప్రీమియం నాణ్యత మొక్కలు మరియు తోటల నైపుణ్యం',
      },
      irrigation: {
        title: 'అధునాతన నీటిపారుదల',
        description: 'నీటి-సమర్థ నీటిపారుదల వ్యవస్థలు మరియు నిర్వహణ',
      },
      machinery: {
        title: 'పొలం యంత్రాలు మరియు పరికరాలు',
        description: 'ఆధునిక వ్యవసాయ పరికరాలు మరియు పరికరాల సరఫరా',
      },
      marketing: {
        title: 'మార్కెటింగ్ మరియు కోత తర్వాత',
        description: 'మార్కెట్ లింకేజ్ మరియు విలువ జోడింపు సేవలు',
      },
      learnMore: 'మరింత తెలుసుకోండి',
    },
    whyChoose: {
      title: 'కల్పకృషిని ఎందుకు ఎంచుకోవాలి',
      subtitle: 'భారతదేశంలో ప్రముఖ వ్యవసాయ సాంకేతిక ఆవిష్కరణ',
      expert: {
        title: 'నిపుణుల బృందం',
        description: '15+ సంవత్సరాల వ్యవసాయ నైపుణ్యం',
      },
      technology: {
        title: 'అధునాతన సాంకేతికత',
        description: 'తాజా IoT మరియు AI పరిష్కారాలు',
      },
      support: {
        title: '24/7 మద్దతు',
        description: 'గడియారం చుట్టూ రైతు సహాయం',
      },
      proven: {
        title: 'నిరూపితమైన ఫలితాలు',
        description: '500+ విజయవంతమైన ప్రాజెక్టులు',
      },
    },
    projects: {
      title: 'మా విజయ కథలు',
      subtitle: 'భారతదేశం అంతటా నిజమైన పరివర్తనలు',
      viewProject: 'వివరాలను చూడండి',
      list: [
        {
          title: 'మిశ్రమ వ్యవసాయ లేఅవుట్ - 50 హెక్టార్లు',
          location: 'కర్ణాటక',
          type: 'వ్యవసాయ అభివృద్ధి'
        },
        {
          title: 'డ్రిప్ నీటిపారుదల వ్యవస్థ డిజైన్',
          location: 'తిపటూర్ ప్రాంతం',
          type: 'నీటిపారుదల'
        },
        {
          title: 'వాణిజ్య పాలిహవుస్ సెటప్',
          location: 'హాసన్ జిల్లా',
          type: 'ప్రాథమిక సదుపాయాలు'
        },
        {
          title: 'ఖచ్చితమైన వ్యవసాయం - డ్రోన్ సేవలు',
          location: 'బహుళ ప్రాంతాలు',
          type: 'సాంకేతికత'
        },
        {
          title: 'మట్టి పరీక్ష మరియు సలహా కార్యక్రమం',
          location: 'తుమకూర్ ప్రాంతం',
          type: 'సలహా'
        },
        {
          title: 'ఆధునిక పరికరాల అమలు',
          location: 'శారదా నగర్, తిపటూర్',
          type: 'పరికరాలు'
        }
      ]
    },
    about: {
      title: 'కల్పకృషి గురించి',
      subtitle: 'స్థిరమైన వ్యవసాయానికి మార్గదర్శకత్వం',
      description: 'కల్పకృషి వినూత్న సాంకేతికత మరియు స్థిరమైన పద్ధతుల ద్వారా భారతీయ వ్యవసాయంలో విప్లవం చేస్తోంది. మెరుగైన ఉత్పాదకత మరియు లాభదాయకత కోసం అత్యాధునిక పరిష్కారాలతో మేము రైతులకు అధికారం ఇస్తాము.',
      mission: {
        title: 'మా లక్ష్యం',
        text: 'భారతీయ వ్యవసాయాన్ని ఆధునికీకరించడానికి అత్యాధునిక సాంకేతికత, నిపుణుల మార్గదర్శకత్వం మరియు స్థిరమైన వ్యవసాయ పరిష్కారాలను అందించడం.'
      },
      vision: {
        title: 'మా దృష్టిభంగి',
        text: 'స్మార్ట్, లాభదాయక మరియు స్థిరమైన వ్యవసాయ పరిస్థితులను నిర్మించడంలో భారతదేశం యొక్క విశ్వసనీయమైన కৃషిచర సాంకేతిక భాగస్వామి అవ్వడం.'
      },
      stats: {
        projects: 'ప్రాజెక్టులు పూర్తయ్యాయి',
        farmers: 'రైతులకు సహాయం చేశాము',
        hectares: 'హెక్టార్లు మార్చబడ్డాయి',
        states: 'రాష్ట్రాలు కవర్ చేయబడ్డాయి',
      },
    },
    irrigation: {
      title: 'అధునాతన నీటిపారుదల పరిష్కారాలు',
      subtitle: 'నీటి-సమర్థ వ్యవసాయ వ్యవస్థలు',
      drip: 'డ్రిప్ నీటిపారుదల',
      sprinkler: 'స్ప్రింక్లర్ వ్యవస్థలు',
      automation: 'స్మార్ట్ ఆటోమేషన్',
      getQuote: 'కొటేషన్ పొందండి',
    },
    digitalApp: {
      title: 'కల్పకృషి డిజిటల్ యాప్',
      subtitle: 'మీ వేలి కొనల వద్ద పొలం నిర్వహణ',
      monitoring: 'రియల్-టైమ్ పర్యవేక్షణ',
      weather: 'వాతావరణ అంచనాలు',
      advisory: 'నిపుణుల సలహా',
      marketplace: 'మార్కెట్‌ప్లేస్',
      download: 'ఇప్పుడు డౌన్‌లోడ్ చేయండి',
    },
    globalServices: {
      title: 'ప్రపంచవ్యాప్తంగా సేవలు',
      subtitle: 'మేము ప్రపంచస్తాయి వ్యవసాయ పరిష్కారాలను అందిస్తున్నాము',
      list: [
        { title: 'ప్రపంచ స్థాయి సేవ', description: 'మేము అనేక ప్రాంతాలలో ప్రొఫెషనల్ సేవలు అందిస్తున్నాము.' },
        { title: 'అవార్డు విజేత నాణ్యత', description: 'నవీకరణ మరియు స్థిరమైన కార్యకలాపాలకు గుర్తింపు.' },
        { title: 'గ్రాహక కేంద్రీకృత', description: 'మీ సంతృప్తి మా ప్రాధాన్యం.' }
      ]
    },
    testimonials: {
      title: 'రైతులు ఏమి చెబుతున్నారు',
      subtitle: 'భారతదేశం అంతటా వేలాది మంది విశ్వసించారు',
      list: [
        { name: 'రాజేష్ కుమార్', role: 'రైతు - 30 ఎకరాలు', location: 'టిప్టూర్, కర్నాటక', text: 'Kalpakrushi నా పొలాన్ని పూర్తిగా మార్చింది. డ్రిప్ ఇరిగేషన్ నీటిని రక్షించి దిగుబడిని పెంచింది.' },
        { name: 'మంజునాథ్ గౌడా', role: 'వాణిజ్య రైతు', location: 'హాసన్ జిల్లా', text: 'డ్రోన్స్ మరియు ప్రిసిషన్ వ్యవసాయం మా పనిని మెరుగుపరచింది.' },
        { name: 'లక్ష్మీ దేవి', role: 'ఆర్గానిక్ ఫార్మ్ యజమాని', location: 'తుమకూర్', text: 'Kalpakrushi మా ఫార్మ్ అవసరాలకు స్థిరమైన పరిష్కారాలను అందించింది.' },
        { name: 'సురేష్ రెడ్డి', role: 'బహు-పంట రైతు', location: 'శారదా నగర', text: 'సబ్సిడీ మార్గదర్శకం మరియు పాలిహౌస్ సెటప్ నా ఆదాయాన్ని పెంచింది.' }
      ]
    },
    contact: {
      title: 'సంప్రదించండి',
      subtitle: 'మీ పొలాన్ని మార్చడానికి సిద్ధంగా ఉన్నారా? ఉచిత సంప్రదింపులు మరియు కస్టమ్ వ్యవసాయ పరిష్కారాల కోసం ఈరోజే మమ్మల్ని సంప్రదించండి',
      form: {
        name: 'మీ పేరు',
        email: 'ఇమెయిల్ చిరునామా',
        phone: 'ఫోన్ నంబర్',
        message: 'మీ సందేశం',
        submit: 'సందేశం పంపండి',
      },
      callUs: 'మమ్మల్ని కాల్ చేయండి',
      emailUs: 'మాకు ఇమెయిల్ చేయండి',
      visitUs: 'మమ్మల్ని సందర్శించండి',
      businessHours: 'వ్యবసాయ సమయం',
      address: 'శారదా నగర్, టిప్టూర్, కర్ణాటక',
    },
    footer: {
      description: 'స్మార్ట్ సాంకేతికత మరియు స్థిరమైన వ్యవసాయ పరిష్కారాలతో భారతీయ వ్యవసాయాన్ని మార్చడం.',
      quickLinks: 'త్వరిత లింక్‌లు',
      services: 'మా సేవలు',
      contactInfo: 'సంప్రదింపు సమాచారం',
      copyright: '© 2025 కల్పకృషి వ్యవసాయ సాంకేతికత. అన్ని హక్కులూ రిజర్వ్ చేయబడ్డాయి.',
      privacy: 'గోప్యతా విధానం',
      terms: 'సేవా నిబంధనలు',
      sitemap: 'సైట్‌మ్యాప్',
    },
    common: {
      readMore: 'మరింత చదవండి',
      getStarted: 'ప్రారంభించండి',
      contactUs: 'మమ్మల్ని సంప్రదించండి',
      viewAll: 'అన్నీ చూడండి',
    },
  },
  mr: {
    nav: {
      home: 'मुख्यपृष्ठ',
      services: 'सेवा',
      about: 'आमच्याबद्दल',
      projects: 'प्रकल्प',
      contact: 'संपर्क',
    },
    hero: {
      title: 'तुमचे शेत बदला',
      titleHighlight: 'स्मार्ट शेतीसह',
      subtitle: 'अत्याधुनिक तंत्रज्ञान, शाश्वत पद्धती आणि जास्तीत जास्त उत्पादनासाठी तज्ञ मार्गदर्शनासह भारतीय शेतीमध्ये क्रांती',
      cta: 'सुरू करा',
      ctaSecondary: 'अधिक जाणून घ्या',
    },
    services: {
      title: 'आमच्या प्रीमियम सेवा',
      subtitle: 'आधुनिक भारतीय शेतीसाठी सर्वसमावेशक कृषी उपाय',
      farmDevelopment: {
        title: 'संपूर्ण शेत विकास',
        description: 'तुमची जमीन उच्च उत्पादकता असलेल्या कृषी मालमत्तेमध्ये बदला',
      },
      soilTesting: {
        title: 'माती चाचणी आणि पीक सल्ला',
        description: 'वैज्ञानिक माती विश्लेषण आणि सानुकूलित पीक शिफारसी',
      },
      precisionAgriculture: {
        title: 'अचूक शेती',
        description: 'अनुकूलित शेती कामकाजासाठी प्रगत तंत्रज्ञान',
      },
      digitalFarming: {
        title: 'डिजिटल शेती उपाय',
        description: 'आधुनिक शेत व्यवस्थापनासाठी स्मार्ट अॅप्स आणि IoT उपकरणे',
      },
      nursery: {
        title: 'रोपवाटिका आणि लागवड',
        description: 'प्रीमियम दर्जाची रोपे आणि लागवड तज्ञता',
      },
      irrigation: {
        title: 'प्रगत सिंचन',
        description: 'पाणी-कार्यक्षम सिंचन प्रणाली आणि व्यवस्थापन',
      },
      machinery: {
        title: 'शेती यंत्रसामग्री आणि उपकरणे',
        description: 'आधुनिक कृषी साधने आणि उपकरणांचा पुरवठा',
      },
      marketing: {
        title: 'विपणन आणि कापणीनंतर',
        description: 'बाजार संपर्क आणि मूल्यवर्धन सेवा',
      },
      learnMore: 'अधिक जाणून घ्या',
    },
    whyChoose: {
      title: 'कल्पकृषि का निवडावे',
      subtitle: 'भारतातील आघाडीचे कृषी तंत्रज्ञान नवकल्पना',
      expert: {
        title: 'तज्ञ संघ',
        description: '15+ वर्षांचे कृषी कौशल्य',
      },
      technology: {
        title: 'प्रगत तंत्रज्ञान',
        description: 'नवीनतम IoT आणि AI उपाय',
      },
      support: {
        title: '24/7 समर्थन',
        description: 'चोवीस तास शेतकरी सहाय्य',
      },
      proven: {
        title: 'सिद्ध परिणाम',
        description: '500+ यशस्वी प्रकल्प',
      },
    },
    projects: {
      title: 'आमच्या यश कथा',
      subtitle: 'भारतभर खरे परिवर्तन',
      viewProject: 'तपशील पहा',
      list: [
        {
          title: 'मिश्र शेती लेआउट - 50 हेक्टर',
          location: 'कर्नाटक',
          type: 'कृषी विकास'
        },
        {
          title: 'ड्रिप सिंचन प्रणाली डिझाइन',
          location: 'टिप्टूर क्षेत्र',
          type: 'सिंचन'
        },
        {
          title: 'व्यावसायिक पॉलीहाउस सेटअप',
          location: 'हासन जिल्हा',
          type: 'पायाभूत सुविधा'
        },
        {
          title: 'अचूक शेती - ड्रोन सेवा',
          location: 'अनेक ठिकाणे',
          type: 'तंत्रज्ञान'
        },
        {
          title: 'माती चाचणी आणि सल्ला कार्यक्रम',
          location: 'तुमकूर क्षेत्र',
          type: 'सल्ला'
        },
        {
          title: 'आधुनिक उपकरणे अंमलबजावणी',
          location: 'शारदा नगर, टिप्टूर',
          type: 'उपकरणे'
        }
      ]
    },
    about: {
      title: 'कल्पकृषी बद्दल',
      subtitle: 'शाश्वत शेतीचे पायनियरिंग',
      description: 'कल्पकृषि नाविन्यपूर्ण तंत्रज्ञान आणि शाश्वत पद्धतींद्वारे भारतीय शेतीमध्ये क्रांती करत आहे. आम्ही वाढीव उत्पादकता आणि नफ्यासाठी अत्याधुनिक उपायांसह शेतकऱ्यांना सक्षम करतो.',
      mission: {
        title: 'आमचे ध्येय',
        text: 'भारतीय शेतीचे आधुनिकीकरण करण्यासाठी अत्याधुनिक तंत्रज्ञान, तज्ञ मार्गदर्शन आणि टिकाऊ शेती समाधान प्रदान करणे.'
      },
      vision: {
        title: 'आमची दृष्टी',
        text: 'स्मार्ट, लाभकारक आणि टिकाऊ कृषी पारिस्थितिकी तंत्र तयार करण्यात भारताचा विश्वासार्ह कृषी तंत्रज्ञान भागीदार बनणे.'
      },
      stats: {
        projects: 'प्रकल्प पूर्ण',
        farmers: 'शेतकऱ्यांना मदत केली',
        hectares: 'हेक्टर रूपांतरित',
        states: 'राज्ये समाविष्ट',
      },
    },
    irrigation: {
      title: 'प्रगत सिंचन उपाय',
      subtitle: 'पाणी-कार्यक्षम शेती प्रणाली',
      drip: 'थेंब सिंचन',
      sprinkler: 'फवारा प्रणाली',
      automation: 'स्मार्ट ऑटोमेशन',
      getQuote: 'कोटेशन मिळवा',
    },
    digitalApp: {
      title: 'कल्पकृषि डिजिटल अॅप',
      subtitle: 'तुमच्या बोटांच्या टोकावर शेत व्यवस्थापन',
      monitoring: 'रिअल-टाइम निरीक्षण',
      weather: 'हवामान अंदाज',
      advisory: 'तज्ञ सल्ला',
      marketplace: 'बाजारपेठ',
      download: 'आता डाउनलोड करा',
    },
    globalServices: {
      title: 'जागतिक सेवा',
      subtitle: 'आम्ही जागतिक दर्जाचे कृषी समाधान देतो',
      list: [
        { title: 'जागतिक सेवा', description: 'आम्ही विविध प्रदेशात व्यावसायिक कृषी सेवा प्रदान करतो.' },
        { title: 'पुरस्कारप्राप्त गुणवत्ता', description: 'नवीन आणि शाश्वत पद्धतींसाठी ओळखलेले.' },
        { title: 'ग्राहक-केंद्रित', description: 'तुमचे समाधान आमचे प्रथम लक्ष्य.' }
      ]
    },
    testimonials: {
      title: 'शेतकरी काय म्हणतात',
      subtitle: 'भारतभर हजारो लोकांचा विश्वास',
      list: [
        { name: 'राजेश कुमार', role: 'शेतकरी - 30 हेक्टर', location: 'टिप्टूर, कर्नाटक', text: 'Kalpakrushi ने माझे शेत पूर्णपणे बदलले. ड्रिप सिंचनाने पाणी वाचवले आणि उत्पन्न वाढले.' },
        { name: 'मंजूनाथ गौडा', role: 'व्यावसायिक शेतकरी', location: 'हसन विभाग', text: 'ड्रोन सेवा आणि प्रिसिजन शेतीने आमचे काम बदलले.' },
        { name: 'लक्ष्मी देवी', role: 'ऑर्गॅनिक फार्म मालक', location: 'तुमकूर', text: 'Kalpakrushi ने आमच्या शेतीसाठी शाश्वत उपाय दिले.' },
        { name: 'सुरेश रेड्डी', role: 'बहु-पिक शेतकरी', location: 'शारदा नगर', text: 'सबसिडी मार्गदर्शन आणि पॉलिहाऊस सेटअपने माझे उत्पन्न वाढले.' }
      ]
    },
    contact: {
      title: 'संपर्कात रहा',
      subtitle: 'तुमचे शेत बदलण्यास तयार आहात? मोफत सल्लामसलत आणि सानुकूल शेती उपायांसाठी आजच आमच्याशी संपर्क साधा',
      form: {
        name: 'तुमचे नाव',
        email: 'ईमेल पत्ता',
        phone: 'फोन नंबर',
        message: 'तुमचा संदेश',
        submit: 'संदेश पाठवा',
      },
      callUs: 'आम्हाला कॉल करा',
      emailUs: 'आम्हाला ईमेल करा',
      visitUs: 'आम्हाला भेट द्या',
      businessHours: 'व्यवसायिक वेळ',
      address: 'शारदा नगर, टिप्टूर, कर्नाटक',
    },
    footer: {
      description: 'स्मार्ट तंत्रज्ञान आणि शाश्वत शेती उपायांसह भारतीय शेतीचे रूपांतर करणे.',
      quickLinks: 'द्रुत दुवे',
      services: 'आमच्या सेवा',
      contactInfo: 'संपर्क माहिती',
      copyright: '© 2025 कल्पकृषि कृषी तंत्रज्ञान. सर्व हक्क राखीव.',
      privacy: 'गोपनीयता धोरण',
      terms: 'सेवा अटी',
      sitemap: 'साइटमॅप',
    },
    common: {
      readMore: 'अधिक वाचा',
      getStarted: 'सुरू करा',
      contactUs: 'आमच्याशी संपर्क साधा',
      viewAll: 'सर्व पहा',
    },
  },
};